import { useEffect } from "react";
import usePagination from "./usePagination";
import { useQuery, useSubscription } from "@apollo/client";
import { IWeatherStationStatus, IWeatherStationStatusIndexParams } from "models/WeatherStationStatus";
import { GET_WEATHER_STATION_STATUS } from "graphql/queries/weather_station_status";
import { OnLastWeatherStationData } from "graphql/subscriptions/weather_station_status";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

const useWeatherStationStatuses = ({
    weather_station_id,
    page: externalPage = DEFAULT_PAGE,
    pageSize: externalPageSize = DEFAULT_PAGE_SIZE,
    orderBy = "desc",
}: IWeatherStationStatusIndexParams) => {

    const hookWeatherStationStatus = usePagination<IWeatherStationStatus>({
        page: externalPage,
        pageSize: externalPageSize,
        sortOrder: orderBy as "desc" | "asc"
    });

    const { page, pageSize } = hookWeatherStationStatus;
    const { data, loading } = useQuery(GET_WEATHER_STATION_STATUS(weather_station_id, page, pageSize, orderBy), {
        // pollInterval: 1000*60, //Actualiza cada minuto
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });

    const { data: lastDataWeatherStationStatus } = useSubscription(OnLastWeatherStationData, { variables: { weather_station_id } });

    useEffect(() => {
        if (lastDataWeatherStationStatus) {
            const { its_weather_station_statuses } = lastDataWeatherStationStatus;
            hookWeatherStationStatus.addElement(its_weather_station_statuses.map((item: IWeatherStationStatus) => ({
                ...item,
                temperatura_aire: item.temperatura_aire / 10,
                temperatura_max: item.temperatura_max / 10,
                temperatura_min: item.temperatura_min / 10,
                humedad_relativa: item.humedad_relativa,
                presion: item.presion / 10,
                viento_velocidad: item.viento_velocidad,
                punto_rocio: item.punto_rocio * 10,
                created_at: item.created_at
            }))[0])
        }
    }, [lastDataWeatherStationStatus]);

    useEffect(() => {
        if (loading) {
            hookWeatherStationStatus.loadingStart();
        } else {
            hookWeatherStationStatus.loadingEnd();
        }

        if (data) {
            const { its_weather_station_statuses: dataWeatherStationStatus, its_weather_station_statuses_aggregate } = data;
            const { count: totalItems } = its_weather_station_statuses_aggregate.aggregate;
            hookWeatherStationStatus.setData(dataWeatherStationStatus.map((item: IWeatherStationStatus) => ({
                ...item,
                temperatura_aire: item.temperatura_aire / 10,
                temperatura_max: item.temperatura_max / 10,
                temperatura_min: item.temperatura_min / 10,
                humedad_relativa: item.humedad_relativa,
                presion: item.presion / 10,
                viento_velocidad: item.viento_velocidad,
                punto_rocio: item.punto_rocio * 10,
                created_at: item.created_at
            })));
            hookWeatherStationStatus.setTotalItems(totalItems);
        }
    }, [data, loading]);

    return hookWeatherStationStatus;
};

export default useWeatherStationStatuses;
